import React, { memo, useMemo, useState } from "react";

import Grid from "@material-ui/core/Grid";
import { useSelector } from "react-redux";

import {
  ControlledLifeCycleInput,
  DollarDisplayCentsInputProps,
  SelectField,
  withNamespace,
} from "components/Form/FormikControls";

import { PAYMENT_METHOD_OPTIONS, PaymentMethod } from "constants/payments";
import { useFieldValue } from "hooks";
import { useEnsureBillingRunLoaded } from "hooks/useEnsureBillingRunLoaded";
import { useShowBillingDocumentField } from "hooks/useHasPermission";
import { formatBillingDocumentLabel } from "lib/billingDocument";
import {
  getAssignableBillingDocumentsByBuyerId,
  getBillingDocumentById,
  getBusinesses,
  selectBillingDocumentOwingDollarsByIdLookup,
} from "selectors";
import { CashPaymentMethodForm } from "./PaymentMethodForm/CashPaymentMethodForm";
import { CardPaymentMethodForm } from "./PaymentMethodForm/CardPaymentMethodForm";
import { CommonPaymentFields } from "./PaymentMethodForm/CommonPaymentMethodFields";

interface EditPaymentFormProps {
  namespace?: string;
}

const PaymentMethodComponentMap = {
  [PaymentMethod.CASH]: CashPaymentMethodForm,
  [PaymentMethod.CARD]: CardPaymentMethodForm,
};

// Snapshots the initial billing document id on mount, before the user picks
// a different one. Without it, picking a candidate would shrink the option
// pool and make the field flicker mid-selection.
function useBillingDocumentOptionPool(ns: string): BillingDocument[] {
  const fromBusinessId: string = useFieldValue(
    withNamespace(ns, "fromBusinessId"),
  );
  const currentBillingDocumentId: string = useFieldValue(
    withNamespace(ns, "billingDocumentId"),
  );
  const [initialBillingDocumentId] = useState(currentBillingDocumentId);

  const assignableBillingDocuments = useSelector<State, BillingDocument[]>(
    getAssignableBillingDocumentsByBuyerId(fromBusinessId),
  );
  const initialBillingDocument = useSelector<State, BillingDocument | null>(
    getBillingDocumentById(initialBillingDocumentId),
  );

  // Always includes the payment's original document, even once fully paid.
  // The field must never hide the document a payment is currently on.
  return useMemo(() => {
    if (
      initialBillingDocument &&
      !assignableBillingDocuments.some(
        billingDocument => billingDocument.id === initialBillingDocument.id,
      )
    ) {
      return [...assignableBillingDocuments, initialBillingDocument];
    }
    return assignableBillingDocuments;
  }, [assignableBillingDocuments, initialBillingDocument]);
}

function useBillingDocumentField(ns: string) {
  const billingDocuments = useBillingDocumentOptionPool(ns);

  const showBillingDocumentFeature = useShowBillingDocumentField();
  useEnsureBillingRunLoaded(showBillingDocumentFeature);
  // A single document leaves nothing to pick between.
  const showBillingDocumentField =
    showBillingDocumentFeature && billingDocuments.length > 1;

  // Picking no document is a valid choice: the backend auto-attaches a
  // best-fit document by amount when none is set (Payment.attach_clearing_sale_billing_document),
  // and never overrides one a person chose here.
  const owingDollarsByBillingDocumentId = useSelector<
    State,
    Record<string, number>
  >((state: State) => selectBillingDocumentOwingDollarsByIdLookup(state));

  return {
    showBillingDocumentField,
    billingDocuments,
    owingDollarsByBillingDocumentId,
  };
}

function EditPaymentFormComponent(props: EditPaymentFormProps) {
  const { namespace: ns = "" } = props;

  const paymentMethod: PaymentMethod = useFieldValue(
    withNamespace(ns, "paymentMethod"),
  );

  const paymentAmountCents: number = useFieldValue(
    withNamespace(ns, "totalAmountCents"),
  );

  const {
    showBillingDocumentField,
    billingDocuments,
    owingDollarsByBillingDocumentId,
  } = useBillingDocumentField(ns);
  const businessesById = useSelector<State, Record<string, Business>>(
    getBusinesses,
  );

  const PaymentMethodFormComponent = PaymentMethodComponentMap[paymentMethod];

  return (
    <>
      <Grid item sm={6}>
        <ControlledLifeCycleInput
          {...DollarDisplayCentsInputProps}
          label="Payment amount ($)"
          name={withNamespace(ns, "totalAmountCents")}
          required
        />
      </Grid>
      <Grid item sm={6}>
        <SelectField
          label="Method"
          name={withNamespace(ns, "paymentMethod")}
          required
          options={PAYMENT_METHOD_OPTIONS}
        />
      </Grid>
      {showBillingDocumentField && (
        <Grid item sm={12}>
          <SelectField
            label="Billing Document"
            name={withNamespace(ns, "billingDocumentId")}
            isClearable
            options={billingDocuments.map(billingDocument => ({
              value: billingDocument.id,
              label: formatBillingDocumentLabel(
                billingDocument,
                businessesById[billingDocument.issuerBusinessId],
                owingDollarsByBillingDocumentId[billingDocument.id],
              ),
            }))}
          />
        </Grid>
      )}
      {PaymentMethodFormComponent ? (
        <PaymentMethodFormComponent
          namespace={ns}
          paymentAmountCents={paymentAmountCents}
        />
      ) : (
        <CommonPaymentFields namespace={ns} />
      )}
    </>
  );
}

export const EditPaymentForm = memo(EditPaymentFormComponent);
